import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/LanguageContext';

const NewsletterSection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('newsletter.errorMessage'));
      return;
    }

    setSubmitted(true);
    setError('');

    // Reset form after 3 seconds
    setTimeout(() => {
      setEmail('');
      setSubmitted(false);
    }, 3000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section
      className="py-12 md:py-20 px-6 md:px-12"
      style={{
        background: 'linear-gradient(135deg, #FEFFFC 0%, #F5F5F5 100%)',
      }}
    >
      <div className="max-w-2xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="font-filson font-bold text-4xl md:text-5xl text-gray-900 mb-4">
              {t('newsletter.title')}
            </h2>
            <p className="font-filson font-medium text-lg text-gray-600">
              {t('newsletter.subtitle')}
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            variants={itemVariants}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <div>
              <label
                className="font-poppins font-medium text-sm text-gray-700 mb-2 block"
                htmlFor="email"
              >
                {t('newsletter.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                placeholder={t('newsletter.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                className="input-bottom-border"
                style={{
                  fontSize: '1rem',
                  borderBottom: '1px solid #E7EAE5',
                  padding: '12px 0',
                  fontFamily: "'Filson Soft', sans-serif",
                }}
                disabled={submitted}
              />
              {error && (
                <p className="text-primary text-sm mt-2 font-filson">{error}</p>
              )}
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              whileHover={{ scale: submitted ? 1 : 1.02 }}
              whileTap={{ scale: submitted ? 1 : 0.98 }}
              disabled={submitted}
              className="btn-primary w-full"
              style={{
                marginTop: '8px',
                opacity: submitted ? 0.7 : 1,
              }}
            >
              {submitted ? t('newsletter.successMessage') : t('newsletter.submitButton')}
            </motion.button>
          </motion.form>

          {/* Success Message */}
          {submitted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
            >
              <p className="font-filson font-medium text-sm text-green-800">
                {t('newsletter.confirmMessage')}
              </p>
            </motion.div>
          )}

          {/* Privacy Notice */}
          <motion.p
            variants={itemVariants}
            className="text-center text-sm text-gray-500 mt-6 font-filson"
          >
            {t('newsletter.privacyNotice')}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterSection;
